import React, { useEffect, useState, useRef, useCallback } from 'react';
import { AddButton, AddColumnButton, BoardContainer, Card, Column, ColumnHeader, ColumnsContainer, ColumnTitle, ColumnCards } from './styles';
import BoardCard from '../boardCard';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, CircularProgress, Typography, TextField, IconButton, Button, Menu, MenuItem, ListItemIcon, ListItemText, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Tooltip } from '@mui/material';
import { projectService } from '../../services/project';
import { Board as BoardInterface, Card as CardInterface, Column as ColumnInterface, ApiCard, ApiColumn } from '../../interfaces/board';
// import EditBoardModal from '../editBoardModal'; // Não é mais necessário
import { useAuth } from '../../contexts/AuthContext';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { useBoardPermissions } from '../../hooks/useBoardPermissions';
import { useToast } from '../../contexts/ToastContext';

interface IBoardProps {
  id: string;
}

export default function Board({ id }: IBoardProps) {
  const { showError, showSuccess } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [boardData, setBoardData] = useState<BoardInterface | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragItem, setDragItem] = useState<{ type: 'column' | 'card', columnId: string, cardId?: string } | null>(null);
  const [dropTarget, setDropTarget] = useState<{ columnId: string, cardId?: string, position?: 'top' | 'bottom' } | null>(null);
  const [showColumnInput, setShowColumnInput] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  
  // Estado para o menu da coluna
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedColumnId, setSelectedColumnId] = useState<string | null>(null);
  const columnMenuOpen = Boolean(columnMenuAnchor);
  
  // Estado para o modal de confirmação de exclusão
  const [deleteColumnConfirmOpen, setDeleteColumnConfirmOpen] = useState(false);
  const [columnToDelete, setColumnToDelete] = useState<{id: string, title: string} | null>(null);
  
  // Estado para controle de edição de título da coluna
  const [editingColumnId, setEditingColumnId] = useState<string | null>(null);
  const [editingColumnTitle, setEditingColumnTitle] = useState('');
  const columnTitleInputRef = useRef<HTMLInputElement>(null);
  
  // Não precisamos mais deste estado, pois o modal de edição foi movido para a página do projeto
  
  const { user } = useAuth();
  // Obter projeto para verificar permissões do usuário
  const [projectData, setProjectData] = useState<any>(null);
  const projectPermissions = useUserPermissions(projectData);
  
  // Usar permissões específicas do board, considerando as permissões do projeto
  const boardPermissions = useBoardPermissions(boardData, projectPermissions);
  
  // Focus the input field when it appears
  useEffect(() => {
    if (showColumnInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showColumnInput]);

  // Efeito para focar no input de edição de título quando ele aparecer
  useEffect(() => {
    if (editingColumnId && columnTitleInputRef.current) {
      columnTitleInputRef.current.focus();
      // Seleciona todo o texto ao focar
      columnTitleInputRef.current.select();
    }
  }, [editingColumnId]);
  
  // Handler para detectar cliques fora do formulário
  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (formRef.current && !formRef.current.contains(event.target as Node) && showColumnInput) {
      handleCancelAddColumn();
    }
  }, [showColumnInput]);
  
  // Efeito para lidar com a tecla ESC e clicks fora do formulário para cancelar a adição de coluna
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showColumnInput) {
        handleCancelAddColumn();
      }
    };
    
    // Adiciona os event listeners
    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);
    
    // Remove os event listeners ao desmontar
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnInput, handleClickOutside]);
  
  // A função de verificar permissões foi substituída pelo hook useUserPermissions
  

  
  // Este método não é mais necessário, pois o modal de edição foi movido para a página do projeto

  useEffect(() => {
    const fetchBoardData = async () => {
      setIsLoading(true);
      try {
        // Buscar o quadro
        const board = await projectService.getBoardById(id);
        
        // Buscar o projeto para verificar permissões com o hook useUserPermissions
        try {
          const project = await projectService.getProjectById(board.projectId);
          setProjectData(project);
        } catch (error) {
          console.error('Erro ao buscar dados do projeto para permissões:', error);
        }
        
        // Formatar os dados do quadro para o formato esperado pelo componente
        const boardWithColumns: BoardInterface = {
          id: board.id,
          title: board.title,
          description: board.description,
          backgroundColor: board.backgroundColor,
          projectId: board.projectId,
          userAccessLevel: board.userAccessLevel,
          hasDirectAccess: board.hasDirectAccess,
          boardUsers: board.boardUsers,
          columns: []
        };
        
        // Ordenar as colunas por posição
        const columnsArray = Array.isArray(board.columns) ? board.columns : [];
        const sortedColumns = columnsArray.sort((a: any, b: any) => {
          // Se a ou b não tiver position definido, atribuímos um valor alto para ficar no final
          const posA = typeof a.position === 'number' ? a.position : 999;
          const posB = typeof b.position === 'number' ? b.position : 999;
          return posA - posB;
        });
        
        // Adicionar colunas ao quadro
        const columnsWithCards: ColumnInterface[] = [];
        
        // Buscar os cartões de cada coluna individualmente
        for (const column of sortedColumns) {
          try {
            const cards = await projectService.getCardsByColumnId(column.id);
            
            // Ordenar os cartões por posição
            const sortedCards = cards.sort((a, b) => {
              const posA = typeof a.position === 'number' ? a.position : 999;
              const posB = typeof b.position === 'number' ? b.position : 999;
              return posA - posB;
            });
            
            columnsWithCards.push({
              id: column.id,
              title: column.title || ((column as any).name || 'Sem título'),
              position: typeof column.position === 'number' ? column.position : 0,
              cards: sortedCards
            });
          } catch (error) {
            console.error(`Erro ao buscar cartões da coluna ${column.id}:`, error);
            // Adicionar a coluna mesmo sem cartões
            columnsWithCards.push({
              id: column.id,
              title: column.title || ((column as any).name || 'Sem título'),
              position: typeof column.position === 'number' ? column.position : 0,
              cards: []
            });
          }
        }
        
        boardWithColumns.columns = columnsWithCards;
        setBoardData(boardWithColumns);
        setIsLoading(false);
      } catch (err) {
        setError("Erro ao carregar o quadro. Tente novamente mais tarde.");
        setIsLoading(false);
        console.error("Erro ao buscar dados do quadro:", err);
      }
    };

    fetchBoardData();
  }, [id]);

  // Função para iniciar a edição do título da coluna
  const handleStartEditingColumn = (columnId: string, currentTitle: string) => {
    // Verificar se o usuário tem permissão para editar (não pode ser observador)
    if (!boardPermissions.canEdit) {
      // Se não for administrador, mostra um tooltip temporariamente
      // Seleciona o elemento do título da coluna
      const columnTitleEl = document.getElementById(`column-title-${columnId}`);
      
      if (columnTitleEl) {
        // Cria o elemento de tooltip
        const tooltip = document.createElement('div');
        tooltip.style.position = 'absolute';
        tooltip.style.zIndex = '9999';
        tooltip.style.backgroundColor = 'rgba(97, 97, 97, 0.9)';
        tooltip.style.color = 'white';
        tooltip.style.padding = '8px 12px';
        tooltip.style.borderRadius = '4px';
        tooltip.style.fontSize = '0.75rem';
        tooltip.style.lineHeight = '1.4';
        tooltip.style.top = `${columnTitleEl.offsetTop - 35}px`;
        tooltip.style.left = `${columnTitleEl.offsetLeft + columnTitleEl.offsetWidth / 2 - 120}px`;
        tooltip.style.width = '240px';
        tooltip.style.textAlign = 'center';
        tooltip.innerText = 'Apenas administradores podem trocar o nome da coluna';
        
        // Adiciona o tooltip à página
        document.body.appendChild(tooltip);
        
        // Remove após 2 segundos
        setTimeout(() => {
          if (tooltip.parentNode) {
            document.body.removeChild(tooltip);
          }
        }, 2000);
      }
      
      return;
    }
    
    setEditingColumnId(columnId);
    setEditingColumnTitle(currentTitle);
  };

  // Função para salvar o título da coluna editado
  const handleSaveColumnTitle = async () => {
    if (!editingColumnId || !boardData) return;
    
    const newTitle = editingColumnTitle.trim();
    if (!newTitle) {
      // Se o título estiver vazio, cancela a edição
      setEditingColumnId(null);
      return;
    }
    
    try {
      // Encontra a coluna sendo editada
      const columnToUpdate = boardData.columns.find(col => col.id === editingColumnId);
      if (!columnToUpdate) return;
      
      // Se o título não mudou, apenas cancela a edição
      if (columnToUpdate.title === newTitle) {
        setEditingColumnId(null);
        return;
      }
      
      // Atualiza localmente para feedback imediato
      const updatedColumns = boardData.columns.map(col => {
        if (col.id === editingColumnId) {
          return {
            ...col,
            title: newTitle
          };
        }
        return col;
      });
      
      setBoardData({
        ...boardData,
        columns: updatedColumns
      });
      
      // Limpa o estado de edição
      setEditingColumnId(null);
      
      // Salva no backend
      await projectService.updateColumn(editingColumnId, {
        title: newTitle
      });
    } catch (error) {
      console.error("Erro ao atualizar título da coluna:", error);
      // Em caso de erro, poderíamos desfazer a mudança local e mostrar uma notificação
    }
  };

  // Função para cancelar a edição do título
  const handleCancelEditColumn = () => {
    setEditingColumnId(null);
    setEditingColumnTitle('');
  };

  const handleAddCard = async (columnId: string) => {
    if (!boardData) return;
    
    try {
      // Criar novo cartão no backend
      const newCard = await projectService.createCard(columnId, {
        title: "Novo cartão",
        description: "",
        priority: "low",
        position: boardData.columns.find(col => col.id === columnId)?.cards.length || 0
      });
      
      
      // Atualizar o estado local
      const updatedColumns = boardData.columns.map(column => {
        if (column.id === columnId) {
          return {
            ...column,
            cards: [...column.cards, newCard]
          };
        }
        return column;
      });
      
      setBoardData({
        ...boardData,
        columns: updatedColumns
      });
      
      // Recarregar os cartões da coluna para garantir consistência
      try {
        const refreshedCards = await projectService.getCardsByColumnId(columnId);
        
        // Ordenar por posição
        const sortedCards = refreshedCards.sort((a, b) => {
          const posA = typeof a.position === 'number' ? a.position : 999;
          const posB = typeof b.position === 'number' ? b.position : 999;
          return posA - posB;
        });
        
        // Atualizar apenas a coluna em questão
        const refreshedColumns = boardData.columns.map(column => {
          if (column.id === columnId) {
            return {
              ...column,
              cards: sortedCards
            };
          }
          return column;
        });
        
        setBoardData({
          ...boardData,
          columns: refreshedColumns
        });
      } catch (error) {
        console.error(`Erro ao recarregar cartões da coluna ${columnId} após adicionar novo card:`, error);
        // Já atualizamos o estado com os dados locais, então não precisa fazer nada aqui
      }
    } catch (error) {
      console.error("Erro ao adicionar cartão:", error);
      showError('Erro ao adicionar cartão. Tente novamente.');
    }
  };
  
  // Função para atualizar um cartão existente
  const handleCardUpdate = async (columnId: string, updatedCard: CardInterface) => {
    if (!boardData) return;
    
    // Verificar se o cartão foi excluído (flag _deleted)
    if ((updatedCard as any)._deleted) {
      
      // Remover o cartão do estado local
      const updatedColumns = boardData.columns.map(column => {
        if (column.id === columnId) {
          return {
            ...column,
            cards: column.cards.filter(card => card.id !== updatedCard.id)
          };
        }
        return column;
      });
      
      setBoardData({
        ...boardData,
        columns: updatedColumns
      });
      
      return; // Não precisamos recarregar os cartões, pois o cartão foi excluído
    }
    
    // Atualizar o estado local para atualizações normais
    const updatedColumns = boardData.columns.map(column => {
      if (column.id === columnId) {
        return {
          ...column,
          cards: column.cards.map(card => 
            card.id === updatedCard.id ? updatedCard : card
          )
        };
      }
      return column;
    });
    
    setBoardData({
      ...boardData,
      columns: updatedColumns
    });
    
    // Recarregar os cartões da coluna para garantir consisência
    try {
      const refreshedCards = await projectService.getCardsByColumnId(columnId);
      
      // Ordenar por posição
      const sortedCards = refreshedCards.sort((a, b) => {
        const posA = typeof a.position === 'number' ? a.position : 999;
        const posB = typeof b.position === 'number' ? b.position : 999;
        return posA - posB;
      });
      
      // Atualizar apenas a coluna em questão
      const refreshedColumns = boardData.columns.map(column => {
        if (column.id === columnId) {
          return {
            ...column,
            cards: sortedCards
          };
        }
        return column;
      });
      
      setBoardData({
        ...boardData,
        columns: refreshedColumns
      });
    } catch (error) {
      console.error(`Erro ao recarregar cartões da coluna ${columnId} após atualização:`, error);
      // Já atualizamos o estado com os dados locais, então não precisa fazer nada aqui
    }
  };

  const handleAddColumn = async () => {
    if (!boardData) return;
    
    // Se não tiver nome, usa um padrão
    const columnName = newColumnName.trim() || "Nova coluna";
    
    try {
      // Criar nova coluna no backend
      const newColumn = await projectService.createColumn(boardData.id, {
        title: columnName,
        position: boardData.columns.length
      });
      
      // Atualizar o estado local
      setBoardData({
        ...boardData,
        columns: [...boardData.columns, newColumn]
      });
      
      // Reset input
      setNewColumnName('');
      setShowColumnInput(false);
    } catch (error) {
      console.error("Erro ao adicionar coluna:", error);
      showError('Erro ao adicionar coluna. Tente novamente.');
    }
  };
  
  const handleCancelAddColumn = () => {
    // Limpar o estado
    setShowColumnInput(false);
    setNewColumnName('');
    
  };
  
  // Funções para gerenciar o menu da coluna
  const handleColumnMenuOpen = (event: React.MouseEvent<HTMLElement>, columnId: string) => {
    event.stopPropagation(); // Evite propagação para não interferir com drag&drop
    setColumnMenuAnchor(event.currentTarget);
    setSelectedColumnId(columnId);
  };
  
  const handleColumnMenuClose = () => {
    setColumnMenuAnchor(null);
    setSelectedColumnId(null);
  };
  
  const handleDeleteColumnConfirm = () => {
    // Encontra a coluna pelo ID selecionado e salva as informações para exibir no modal
    if (!selectedColumnId || !boardData) return;
    
    const column = boardData.columns.find(col => col.id === selectedColumnId);
    if (column) {
      setColumnToDelete({
        id: column.id,
        title: column.title
      });
      // Fecha o menu de opções
      handleColumnMenuClose();
      // Abre o modal de confirmação
      setDeleteColumnConfirmOpen(true);
    }
  };
  
  const handleConfirmDialogClose = () => {
    setDeleteColumnConfirmOpen(false);
    setColumnToDelete(null);
  };
  
  const handleDeleteColumn = async () => {
    if (!columnToDelete || !boardData) return;
    
    try {
      // Fechamos o modal primeiro
      handleConfirmDialogClose();
      
      // Atualizar o estado local imediatamente para feedback visual rápido
      const updatedColumns = boardData.columns.filter(col => col.id !== columnToDelete.id);
      setBoardData({
        ...boardData,
        columns: updatedColumns
      });
      
      // Chamar a API para excluir a coluna
      await projectService.deleteColumn(columnToDelete.id);
      
    } catch (error) {
      console.error("Erro ao excluir coluna:", error);
      // Se houver erro, recarregar os dados do quadro
      try {
        const refreshedBoard = await projectService.getBoardById(boardData.id);
        // Formatar os dados para o formato do componente
        // (reutilizaria o código de formatação que já existe no useEffect, mas simplificando aqui)
        setBoardData({
          ...refreshedBoard,
          columns: refreshedBoard.columns.map((col: any) => ({
            id: col.id,
            title: col.title || ((col as any).name || 'Sem título'),
            position: col.position || 0,
            cards: (col.cards || []).map((card: any) => ({
              id: card.id,
              title: card.name || card.title || 'Sem título',
              description: card.description || '',
              position: card.position || 0,
              priority: card.status === 'High' ? 'high' : 
                       card.status === 'Medium' ? 'medium' : 'low',
              dueDate: card.dueDate,
              assigneeId: card.assignedUser?.id,
              assigneeName: card.assignedUser?.name,
              assigneeAvatar: card.assignedUser?.avatarUrl,
              commentCount: card.commentsCount || 0,
              attachmentCount: card.annexesCount || 0,
              createdAt: card.createdAt,
              updatedAt: card.lastUpdatedAt || card.createdAt
            }))
          }))
        });
      } catch (refreshError) {
        console.error("Erro ao recarregar dados do quadro após falha na exclusão:", refreshError);
        showError('Erro ao excluir coluna. Por favor, tente novamente.');
      }
    }
  };

  const clearDropIndicators = () => {
    document.querySelectorAll('.drag-over-top, .drag-over-bottom').forEach(el => {
      el.classList.remove('drag-over-top', 'drag-over-bottom');
    });
    
    document.querySelectorAll('.column-drag-over').forEach(el => {
      el.classList.remove('column-drag-over');
    });
  };

  const handleColumnDragStart = (e: React.DragEvent<HTMLDivElement>, columnId: string) => {
    // Não iniciar drag se estiver editando alguma coluna ou se não puder mover
    if (editingColumnId || !boardPermissions.canMoveCards) {
      e.preventDefault();
      return;
    }
    
    // Apenas usuários que não são observadores podem arrastar colunas
    setDragItem({ type: 'column', columnId });
    
    const columnEl = document.getElementById(`column-${columnId}`);
    if (columnEl) {
      columnEl.style.opacity = '0.5';
    }
    
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleColumnDragEnd = (e: React.DragEvent<HTMLDivElement>, columnId: string) => {
    const columnEl = document.getElementById(`column-${columnId}`);
    if (columnEl) {
      columnEl.style.opacity = '1';
    }
    
    clearDropIndicators();
    setDragItem(null);
    setDropTarget(null);
  };

  const handleColumnDragOver = (e: React.DragEvent<HTMLDivElement>, columnId: string) => {
    e.preventDefault();
    
    if (!dragItem) return;
    
    if (dragItem.type === 'column') {
      if (dragItem.columnId === columnId) {
        setDropTarget(null);
        return;
      }
      
      setDropTarget({ columnId });
      
      clearDropIndicators();
      const columnEl = document.getElementById(`column-${columnId}`);
      if (columnEl) {
        columnEl.classList.add('column-drag-over');
      }
    }
    
    if (dragItem.type === 'card') {
      if (!e.target || !(e.target as HTMLElement).closest('.card-draggable')) {
        setDropTarget({ columnId });
        
        clearDropIndicators();
        const columnEl = document.getElementById(`column-${columnId}`);
        if (columnEl) {
          columnEl.classList.add('column-drag-over');
        }
      }
    }
  };

  const handleCardDragStart = (e: React.DragEvent<HTMLDivElement>, columnId: string, cardId: string) => {
    // Não iniciar drag se estiver editando alguma coluna ou se não puder mover
    if (editingColumnId || !boardPermissions.canMoveCards) {
      e.preventDefault();
      return;
    }
    
    setDragItem({ type: 'card', columnId, cardId });
    
    e.currentTarget.style.opacity = '0.5';
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleCardDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    e.currentTarget.style.opacity = '1';
    
    clearDropIndicators();
    setDragItem(null);
    setDropTarget(null);
  };

  const handleCardDragOver = (e: React.DragEvent<HTMLDivElement>, columnId: string, cardId: string) => {
    e.preventDefault();
    e.stopPropagation(); 
    
    if (!dragItem || dragItem.type !== 'card') return;
    
    if (dragItem.cardId === cardId && dragItem.columnId === columnId) {
      clearDropIndicators();
      setDropTarget(null);
      return;
    }
    
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY;
    const position = y < rect.top + rect.height / 2 ? 'top' : 'bottom';
    
    setDropTarget({ columnId, cardId, position });
    
    clearDropIndicators();
    
    e.currentTarget.classList.add(`drag-over-${position}`);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>, columnId?: string, cardId?: string) => {
    e.preventDefault();
    
    const effectiveTarget = dropTarget;
    
    if (!dragItem || !effectiveTarget || !boardData || !columnId) {
      clearDropIndicators();
      setDragItem(null);
      setDropTarget(null);
      return;
    }
    
    // Cria uma cópia profunda dos dados para manipulação local
    const newBoardData = JSON.parse(JSON.stringify(boardData)) as BoardInterface;
    
    if (dragItem.type === 'column' && effectiveTarget.columnId) {
      try {
        // Qualquer usuário do projeto pode reordenar colunas, não apenas administradores
        const dragIndex = newBoardData.columns.findIndex(col => col.id === dragItem.columnId);
        const dropIndex = newBoardData.columns.findIndex(col => col.id === effectiveTarget.columnId);
        
        if (dragIndex !== -1 && dropIndex !== -1 && dragIndex !== dropIndex) {
          // Preserve the original board data in case the API call fails
          const originalBoardData = JSON.parse(JSON.stringify(boardData));
          
          // Atualiza localmente
          const [movedColumn] = newBoardData.columns.splice(dragIndex, 1);
          newBoardData.columns.splice(dropIndex, 0, movedColumn);
          
          // Atualiza os índices de posição
          newBoardData.columns.forEach((col, index) => {
            col.position = index;
          });
          
          // Update UI immediately
          setBoardData(newBoardData);
          
          try {
            // Chama a API para persistir a alteração
            await projectService.reorderColumn(
              boardData.id, 
              dragItem.columnId, 
              dropIndex
            );
            
            // Atualizar as posições para corresponder às do servidor
            try {
              const updatedBoard = await projectService.getBoardById(boardData.id);
              if (updatedBoard && updatedBoard.columns) {
                // Manter apenas as posições das colunas atualizadas
                const refreshedColumns = updatedBoard.columns.sort((a: any, b: any) => {
                  const posA = typeof a.position === 'number' ? a.position : 999;
                  const posB = typeof b.position === 'number' ? b.position : 999;
                  return posA - posB;
                });
                
                // Atualizar apenas as posições das colunas, mantendo os cartões já carregados
                const columnsWithUpdatedPositions = refreshedColumns.map((refreshedCol: any) => {
                  const existingCol = newBoardData.columns.find(col => col.id === refreshedCol.id);
                  if (existingCol) {
                    return {
                      ...existingCol,
                      position: refreshedCol.position
                    };
                  }
                  return refreshedCol;
                });
                
                // Atualiza a UI com as posições corretas
                setBoardData({
                  ...newBoardData,
                  columns: columnsWithUpdatedPositions
                });
              }
            } catch (refreshError) {
              console.error("Erro ao atualizar posições das colunas após reordenação:", refreshError);
            }
          } catch (apiError: any) {
            console.error("Error from API while reordering columns:", apiError);
            // Revert to the original data if the API call fails
            setBoardData(originalBoardData);
            
            // Não mostrar mensagem de erro para erros de permissão (403)
            if (!(apiError.response && apiError.response.status === 403)) {
              showError("Não foi possível reordenar as colunas. Por favor, tente novamente.");
            }
          }
        }
      } catch (error) {
        console.error("Erro ao reordenar colunas:", error);
        showError('Erro ao reordenar colunas. Tente novamente.');
      }
    }
    
    if (dragItem.type === 'card' && dragItem.cardId) {
      try {
        const sourceColIndex = newBoardData.columns.findIndex(col => col.id === dragItem.columnId);
        const targetColIndex = newBoardData.columns.findIndex(col => col.id === effectiveTarget.columnId);
        
        if (sourceColIndex !== -1 && targetColIndex !== -1) {
          const sourceCol = newBoardData.columns[sourceColIndex];
          const cardIndex = sourceCol.cards.findIndex(card => card.id === dragItem.cardId);
          
          if (cardIndex !== -1) {
            // Remove o cartão da coluna de origem
            const [movedCard] = sourceCol.cards.splice(cardIndex, 1);
            
            // Adiciona o cartão na coluna de destino
            const targetCol = newBoardData.columns[targetColIndex];
            
            let insertIndex = targetCol.cards.length;
            
            if (effectiveTarget.cardId) {
              const targetCardIndex = targetCol.cards.findIndex(card => card.id === effectiveTarget.cardId);
              
              if (targetCardIndex !== -1) {
                insertIndex = effectiveTarget.position === 'top' ? targetCardIndex : targetCardIndex + 1;
              }
            }
            
            targetCol.cards.splice(insertIndex, 0, movedCard);
            
            // Atualiza as posições dos cartões
            targetCol.cards.forEach((card, index) => {
              card.position = index;
            });
            
            // Atualiza a UI imediatamente
            setBoardData(newBoardData);
            
            try {
              if (dragItem.columnId === effectiveTarget.columnId) {
                // Se for na mesma coluna, é apenas reordenação
                await projectService.reorderCard(
                  dragItem.columnId,
                  dragItem.cardId,
                  insertIndex
                );
              } else {
                // Se for para outra coluna, é movimentação
                await projectService.moveCard(
                  dragItem.cardId,
                  effectiveTarget.columnId,
                  insertIndex
                );
              }
            } catch (apiError: any) {
              // Não mostrar mensagem de erro para erros de permissão (403)
              if (apiError.response && apiError.response.status === 403) {
              }
            }
          }
        }
      } catch (error) {
        console.error("Erro ao mover cartão:", error);
        showError('Erro ao mover cartão. Tente novamente.');
      }
    }
    
    clearDropIndicators();
    setDragItem(null);
    setDropTarget(null);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !boardData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Typography color="error">{error || "Dados do quadro não encontrados"}</Typography>
      </Box>
    );
  }

  return (
    <BoardContainer backgroundColor={boardData.backgroundColor}>
      {/* Menu de opções do Board */}
      {/* Modal de edição do Board - Não utilizado mais neste componente */}
      <style>
        {`
          .drag-over-top {
            border-top: 2px solid #2196f3 !important;
          }
          .drag-over-bottom {
            border-bottom: 2px solid #2196f3 !important;
          }
          .column-drag-over {
            box-shadow: inset 0 -3px 0 #2196f3 !important;
          }
        `}
      </style>
      <ColumnsContainer>
        {boardData.columns.map((column) => (
          <Column 
            key={column.id}
            id={`column-${column.id}`}
            onDragOver={(e) => handleColumnDragOver(e, column.id)}
            onDrop={(e) => handleDrop(e, column.id)}
          >
            {editingColumnId === column.id ? (
                <ColumnHeader
                  draggable={false}
                  style={{ cursor: 'default' }}
                >
                  <TextField
                    inputRef={columnTitleInputRef}
                    fullWidth
                    value={editingColumnTitle}
                    onChange={(e) => setEditingColumnTitle(e.target.value)}
                    variant="standard"
                    size="small"
                    onBlur={handleSaveColumnTitle}
                    onKeyDown={(e) => {
                      e.stopPropagation(); // Evita propagação para o quadro
                      if (e.key === 'Enter') {
                        handleSaveColumnTitle();
                      } else if (e.key === 'Escape') {
                        handleCancelEditColumn();
                      }
                    }}
                    sx={{
                      '& input': { 
                        color: 'white',
                        fontSize: '1rem',
                        fontWeight: 500,
                        padding: '4px 8px',
                        '&:focus': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        }
                      },
                      '& .MuiInput-underline:before': { borderBottomColor: 'transparent' },
                      '& .MuiInput-underline:after': { borderBottomColor: '#1976d2' },
                    }}
                  />
                </ColumnHeader>
              ) : (
                <ColumnHeader
                    draggable={!editingColumnId} // Desabilita drag quando estiver editando
                    onDragStart={(e) => handleColumnDragStart(e, column.id)}
                    onDragEnd={(e) => handleColumnDragEnd(e, column.id)}
                    style={{ 
                      cursor: boardPermissions.canMoveCards ? 'grab' : 'default', // Cursor de arrastar apenas para quem pode
                      // Dica visual apenas para administradores que podem editar o nome
                      borderBottom: boardPermissions.canEdit ? '1px dashed rgba(255,255,255,0.3)' : 'none' 
                    }}
                    onDoubleClick={() => handleStartEditingColumn(column.id, column.title)}
                  >
                    {boardPermissions.canEdit ? (
                      <Tooltip title="Clique duplo para editar o nome" placement="top">
                        <ColumnTitle id={`column-title-${column.id}`} variant="subtitle1">{column.title}</ColumnTitle>
                      </Tooltip>
                    ) : (
                      <ColumnTitle id={`column-title-${column.id}`} variant="subtitle1">{column.title}</ColumnTitle>
                    )}
                    {boardPermissions.canDelete && (
                      <IconButton 
                        size="small" 
                        onClick={(e) => handleColumnMenuOpen(e, column.id)}
                        sx={{ padding: '4px', color: 'rgba(255, 255, 255, 0.7)' }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    )}
                  </ColumnHeader>
              )}
            
            <ColumnCards>
              {column.cards.map((card) => (
                <BoardCard
                  key={card.id}
                  data={card}
                  columnId={column.id}
                  onDragStart={handleCardDragStart}
                  onDragEnd={handleCardDragEnd}
                  onDragOver={handleCardDragOver}
                  onDrop={handleDrop}
                  onCardUpdate={(updatedCard) => handleCardUpdate(column.id, updatedCard)}
                  isObserver={boardPermissions.isObserver}
                />
              ))}
            </ColumnCards>
            
            {boardPermissions.canCreateCards && (
              <AddButton
                startIcon={<AddIcon />}
                onClick={() => handleAddCard(column.id)}
              >
                Adicionar um cartão
              </AddButton>
            )}
          </Column>
        ))}
        
        {boardPermissions.canEdit && (
          <AddColumnButton
            startIcon={!showColumnInput ? <AddIcon /> : null}
            onClick={() => setShowColumnInput(true)} // Apenas mostra o campo, não cria uma coluna
            sx={showColumnInput ? { p: 0, backgroundColor: '#333' } : {}}
          >
          {!showColumnInput ? (
            "Adicionar coluna"
          ) : (
            <Box sx={{ p: 1, width: '100%' }}>
              {/* Envolve o TextField em um form para isolar eventos */}
              <form 
                ref={formRef}
                onSubmit={(e) => {
                  e.preventDefault();
                  handleAddColumn();
                }}
                >
                <TextField
                  inputRef={inputRef}
                  fullWidth
                  variant="outlined"
                  placeholder="Digite o nome da lista..."
                  size="small"
                  value={newColumnName}
                  onChange={(e) => setNewColumnName(e.target.value)}
                  // Desativa o evento de key press completamente
                  onKeyPress={(e) => {
                    // Permitir a digitação normal de todos os caracteres
                    e.stopPropagation();
                  }}
                  // Evita propagação de qualquer tecla e trata Escape corretamente
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    // Cancela ao pressionar Escape
                    if (e.key === 'Escape') {
                      handleCancelAddColumn();
                      // Previne outros comportamentos padrão que possam interferir
                      e.preventDefault();
                    }
                  }}
                  sx={{
                  bgcolor: '#000',
                  borderRadius: 1,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  },
                  '& input': { color: 'white' }
                }}
                autoFocus={true}
              />
              <Box sx={{ display: 'flex', mt: 1, gap: 1, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  size="small"
                  type="submit" /* Mudando para submit para funcionar com o form */
                  sx={{ bgcolor: '#1976d2', color: 'white', textTransform: 'none' }}
                >
                  Adicionar Lista
                </Button>
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancelAddColumn();
                  }} 
                  sx={{ color: 'white' }}
                  type="button" /* Explicitamente definindo como button normal */
                  aria-label="Cancelar"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              </form>
            </Box>
          )}
          </AddColumnButton>
        )}
      </ColumnsContainer>
      
      {/* Menu de opções da coluna */}
      <Menu
        anchorEl={columnMenuAnchor}
        open={columnMenuOpen}
        onClose={handleColumnMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleDeleteColumnConfirm} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Excluir coluna</ListItemText>
        </MenuItem>
      </Menu>
      
      {/* Modal de confirmação para exclusão de coluna */}
      <Dialog
        open={deleteColumnConfirmOpen}
        onClose={handleConfirmDialogClose}
        aria-labelledby="delete-column-dialog-title"
        aria-describedby="delete-column-dialog-description"
      >
        <DialogTitle id="delete-column-dialog-title">
          Excluir coluna
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-column-dialog-description">
            Tem certeza que deseja excluir a coluna "{columnToDelete?.title}"? Esta ação não pode ser desfeita
            e todos os cartões associados a esta coluna serão perdidos permanentemente.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmDialogClose} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleDeleteColumn} color="error" autoFocus>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </BoardContainer>
  );
}